// Deterministic model router
// Maps structured objectives + domain to optimal model variants and service calls.
// The LLM only extracts objectives/domain — this module decides HOW to fulfill them.

import { resolveSpec as _resolveSpec } from './specResolver';
import { MODEL_REGISTRY } from '../registry/modelRegistry';
import { normalizeModelPolicy } from '../../config/capabilities';
import { MATTERGEN_TARGET_PROPERTY_KEYS, mattergenPropertyError } from '../../config/mattergenConditioning';
import { GT4SD_RT_TARGET_RANGE } from '../../config/gt4sdCheckpoints';

// ── Available services and their capabilities ────────────────────────────────

const SERVICES = {
  mattergen: {
    id: 'mattergen',
    tool: 'mattergen-service',
    domains: ['inorganic_crystal'],
    // Variants keyed by checkpoint name → which property they condition on
    variants: {
      mattergen_base:                       { properties: [], description: 'Unconditional generation' },
      mp_20_base:                           { properties: [], description: 'Materials Project 20-element base' },
      dft_band_gap:                         { properties: ['band_gap'], description: 'Band gap conditioning (DFT)' },
      ml_bulk_modulus:                       { properties: ['bulk_modulus'], description: 'Bulk modulus conditioning (ML)' },
      chemical_system:                       { properties: [], requires: ['elements'], description: 'Chemical system conditioning' },
      chemical_system_energy_above_hull:     { properties: ['energy_above_hull'], requires: ['elements'], description: 'Composition + stability' },
      dft_mag_density:                       { properties: ['magnetic_density'], description: 'Magnetic density conditioning' },
      // 1a-02 (open item 2, decision L2): HHI conditions on both keys and is routed only when
      // both targets carry a value that passes mattergenPropertyError (the gate in
      // selectMatterGenVariants); it then replaces plain dft_mag_density.
      dft_mag_density_hhi_score:             { properties: ['magnetic_density', 'hhi_score'], description: 'Magnetic density + HHI score' },
      // 1a-03 (open item 3): space_group conditions on its own key and is routed only for a
      // space_group target with constraint '=' and an integer from 1 to 230 (the gate in
      // selectMatterGenVariants), the one value mattergenPropertiesFor sends.
      space_group:                           { properties: ['space_group'], requires: ['space_group'], description: 'Space group conditioning' },
    },
    // Part 1a: no dispatch builder. buildRow (src/ai/rows/prefillRows.js) writes the
    // checkpoint's properties, guidance and record_trajectories into row.params.
  },
  fm4m: {
    id: 'fm4m',
    tool: 'ibm-materials-service',
    domains: ['polymer', 'small_molecule'],
    variants: {
      bart:      { properties: [], description: 'BART generative (de-novo molecules)' },
      mhg:       { properties: [], description: 'MHG generative (molecular hypergraph)' },
      'smi-ted': { properties: [], description: 'SMI-TED generative' },
      'mol-xl':  { properties: [], description: 'Mol-XL featurization' },
    },
    // Part 1a: no dispatch builder. buildRow writes the generator ('bart'), the RDKit filters
    // derived from spec.targets (fm4mFiltersFromTargets in src/ai/rows/prefillRows.js), the
    // oversample, max_length and the featurization model into row.params.
  },
  gt4sd: {
    id: 'gt4sd',
    tool: 'gt4sd-service',
    domains: ['small_molecule'],
    // Only the two algorithms bundled in the gt4sd-service image. guacamol/reinvent/moler
    // are backend stubs (HTTP 501); re-add here AND in GT4SDForm.jsx once their deps ship.
    variants: {
      // RT-6: no built-in checkpoint. This variant runs only against customer weights,
      // and the router never selects it (selectGT4SDVariant returns RT or nothing).
      hugging_face:           { properties: [], description: 'HuggingFace generative model (de-novo, customer weights only)' },
      // M1: properties mirror the registry — only what the deployed logP RT
      // checkpoint can condition (see modelRegistry.js gt4sd note).
      regression_transformer: { properties: ['logP'], description: 'Property-conditioned generation via Regression Transformer' },
    },
    // Part 1a: no dispatch builder. buildRow writes algorithm_version and, for
    // regression_transformer, the RT target pair (gt4sdConditioningFromTargets in
    // src/ai/rows/prefillRows.js, with the logp wire token from gt4sdTargetToken).
  },
};

/** Coerce a target value into a dispatchable conditioning number: a finite number
 * passes through; a numeric [min,max] pair collapses to its midpoint; anything
 * else (null, undefined, strings, ragged arrays) is undefined = not conditionable. */
function usableConditioningValue(v) {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (Array.isArray(v) && v.length === 2) {
    const [lo, hi] = v.map(Number);
    if (Number.isFinite(lo) && Number.isFinite(hi)) return (lo + hi) / 2;
  }
  return undefined;
}

/** The value a MatterGen property key would be conditioned on for these targets, or undefined
 * (1a-02, decision D35: the one MatterGen value predicate, shared by the router's value gates
 * and by prefill's mattergenPropertiesFor, so a gated checkpoint is routed exactly when
 * prefill can fill it). Target names match by lowercase exact name, never by substring.
 * - space_group: the first target named space_group with constraint '=' whose raw value passes
 *   mattergenPropertyError('space_group', value): an integer 1 to 230, never a midpoint.
 * - chemical_system: always undefined here; it comes from system.elements_in
 *   (mattergenChemicalSystem), not from targets.
 * - every other key: the first target, in spec order, whose lowercase name maps to the key
 *   through MATTERGEN_TARGET_PROPERTY_KEYS and whose usableConditioningValue(value) is defined
 *   and passes mattergenPropertyError(key, v); that v. */
function mattergenConditioningValue(key, targets) {
  const list = Array.isArray(targets) ? targets : [];
  if (key === 'chemical_system') return undefined;
  for (const t of list) {
    const name = String(t?.name || '').toLowerCase();
    if (key === 'space_group') {
      if (name !== 'space_group' || t?.constraint !== '=') continue;
      if (mattergenPropertyError('space_group', t.value) === null) return t.value;
      continue;
    }
    if (!Object.prototype.hasOwnProperty.call(MATTERGEN_TARGET_PROPERTY_KEYS, name)) continue;
    if (MATTERGEN_TARGET_PROPERTY_KEYS[name] !== key) continue;
    const v = usableConditioningValue(t?.value);
    if (v !== undefined && mattergenPropertyError(key, v) === null) return v;
  }
  return undefined;
}

// ── Domain classification ────────────────────────────────────────────────────
// All keyword lists and FAMILY_MAP now live in specResolver.js.
// resolveSpec() must be called before routeSpec() — it writes spec.meta.resolved_domains.
// classifyDomains() reads that pre-resolved list; if missing it runs resolveSpec() as fallback.

// GT4SD conditionable props — derived from the registry, not hardcoded here
const GT4SD_CONDITIONABLE_PROPS = (() => {
  const gt4sd = MODEL_REGISTRY.find(m => m.id === 'gt4sd');
  const names = (gt4sd?.conditionable_properties || []).flatMap(p => [p.name, p.name.toLowerCase()]);
  return new Set(names);
})();

/**
 * Select the best GT4SD variant given objectives.
 * Returns null if GT4SD should not be used.
 */
function selectGT4SDVariant(objectives) {
  // M1: RT is only worth selecting when a conditionable objective carries a
  // usable numeric value — a valueless maximize/minimize would fall into the
  // service's silent logp/2.5 default. Those objectives route to FM4M instead.
  //
  // GT4SD RT Part D: and only when that value is one the checkpoint accepts. The
  // service 422s anything outside GT4SD_RT_TARGET_RANGE, so routing an objective
  // it cannot serve would spend the row on a refusal. Out of range goes to FM4M,
  // exactly like a valueless objective.
  const hasConditionable = (objectives || []).some(o => {
    const n = String(o?.name || '');
    if (!GT4SD_CONDITIONABLE_PROPS.has(n) && !GT4SD_CONDITIONABLE_PROPS.has(n.toLowerCase())) return false;
    const value = usableConditioningValue(o?.value);
    return value !== undefined && value >= GT4SD_RT_TARGET_RANGE[0] && value <= GT4SD_RT_TARGET_RANGE[1];
  });
  if (hasConditionable) return 'regression_transformer';
  return null;
}

function classifyDomains(spec) {
  // Primary path: resolveSpec() already ran and wrote resolved_domains
  if (Array.isArray(spec?.meta?.resolved_domains) && spec.meta.resolved_domains.length > 0) {
    return spec.meta.resolved_domains;
  }
  // Fallback: spec wasn't passed through resolveSpec — run it now
  const resolved = _resolveSpec(spec);
  return resolved.meta.resolved_domains;
}

// ── Variant selection for MatterGen ──────────────────────────────────────────

function selectMatterGenVariants(objectives, elements) {
  const variants = SERVICES.mattergen.variants;
  const hasElements = Array.isArray(elements) && elements.length > 0;
  const objectiveNames = new Set((objectives || []).map(o => String(o?.name || '').toLowerCase()));

  const selected = new Set();
  const coverage = {}; // variant → which objectives it covers

  // Score each variant by how many objectives it covers
  for (const [name, info] of Object.entries(variants)) {
    // Skip variants that require elements if we don't have them
    if (info.requires?.includes('elements') && !hasElements) continue;
    // 1a-03 space_group gate (open item 3): the checkpoint is selected only for a target named
    // space_group with constraint '=' and an integer from 1 to 230, the one value prefill
    // sends; never a range, a midpoint or a symbol such as 'Fm-3m'. Skipped here, before the
    // elements fallback below, so a skipped space_group leaves elements on chemical_system.
    if (name === 'space_group' && mattergenConditioningValue('space_group', objectives) === undefined) continue;
    // 1a-02 HHI gate (decision L2): dft_mag_density_hhi_score needs both values, each passing
    // mattergenPropertyError. Skipped here, before the elements fallback below, so a skipped
    // HHI leaves `selected` empty and elements still fall back to chemical_system.
    if (name === 'dft_mag_density_hhi_score'
      && (mattergenConditioningValue('dft_mag_density', objectives) === undefined
        || mattergenConditioningValue('hhi_score', objectives) === undefined)) continue;

    const covered = info.properties.filter(p => objectiveNames.has(p));
    if (covered.length > 0) {
      selected.add(name);
      coverage[name] = covered;
    }
  }

  // HHI conditions on dft_mag_density too, so plain dft_mag_density would be a duplicate run
  // of the same magnetic target (B11).
  if (selected.has('dft_mag_density_hhi_score')) {
    selected.delete('dft_mag_density');
    delete coverage['dft_mag_density'];
  }

  // If we have elements but no property-based variant covers anything, use chemical_system
  if (hasElements && selected.size === 0) {
    selected.add('chemical_system');
    coverage['chemical_system'] = [];
  }

  // If elements + energy_above_hull objective, prefer the combined variant
  if (hasElements && objectiveNames.has('energy_above_hull')) {
    selected.add('chemical_system_energy_above_hull');
    // Remove plain chemical_system if the combined one is selected
    if (selected.has('chemical_system') && selected.has('chemical_system_energy_above_hull')) {
      selected.delete('chemical_system');
    }
  }

  // Always include at least one variant
  if (selected.size === 0) {
    selected.add('mattergen_base');
    coverage['mattergen_base'] = [];
  }

  return {
    variants: Array.from(selected),
    coverage,
  };
}

// ── Main router ──────────────────────────────────────────────────────────────

// Org model policy (2026-08-22). `weight` is a per-model multiplier on that
// model's candidate budget (no two models ever split one objective's budget
// in this router, so there is no share to renormalize); 0 drops the call and
// is reported as `zero_weight`. A disabled model is removed before routing
// and reported as `org_policy`. A gt4sd that is disabled or at weight 0 falls
// back to fm4m, which can serve small-molecule objectives unconditionally:
// the services refuse weight 0 like a disabled model (R4-4, 2026-10-07), so
// the router treats the two alike, and `fallback` names fm4m only when fm4m
// itself can run. Still deterministic, still LLM-free.
const BUDGET_MAX = 1024;
function applyWeight(budget, weight) {
  if (weight === 1 || budget === 0) return budget;
  return Math.max(1, Math.min(BUDGET_MAX, Math.round(budget * weight)));
}

// R4-4: a model can run only when it is enabled and its weight is above 0
// (normalizeModelPolicy clamps every weight into [WEIGHT_MIN, WEIGHT_MAX]).
/** R4-4: a service runs only when it is enabled AND its weight is above 0. Exported so
 * the swap list (src/ai/rows/compatibleTargets.js, 1b-1) applies the same rule as the
 * router, rather than a second copy of it that could drift. */
export function modelRuns(entry) {
  return !!entry && entry.enabled && entry.weight > 0;
}

/**
 * Route a generation spec to the optimal set of service calls + variants.
 *
 * @param {Object} spec - generation_spec from the plan (objectives, system, constraints, etc.)
 * @param {Object} [opts]
 * @param {string} [opts.budgetSplit='full'] - 'full' = each domain gets totalBudget (default),
 *   'proportional' = totalBudget is divided across domains
 * @param {Object} [opts.modelPolicy] - org policy {modelId: {enabled, weight}}, see src/config/capabilities.js
 * @returns {Object} { calls, domains, totalBudget, skipped }
 */
function routeSpec(spec, opts = {}) {
  const objectives = Array.isArray(spec?.targets) ? spec.targets : [];
  const elements = Array.isArray(spec?.system?.elements_in) ? spec.system.elements_in : [];
  const domains = classifyDomains(spec);
  const totalBudget = Number(spec?.constraints?.num_candidates) || 64;

  const needsCrystal = domains.includes('inorganic_crystal');
  const needsMolecular = domains.includes('polymer') || domains.includes('small_molecule');

  // Budget per domain: full (default) gives each domain the full budget,
  // proportional splits evenly across active domains.
  const domainCount = (needsCrystal ? 1 : 0) + (needsMolecular ? 1 : 0);
  const splitMode = opts.budgetSplit || 'full';
  const domainBudget = (splitMode === 'proportional' && domainCount > 1)
    ? Math.max(8, Math.floor(totalBudget / domainCount))
    : totalBudget;

  const policy = normalizeModelPolicy(opts.modelPolicy);
  const skipped = [];
  const calls = [];

  // MatterGen for crystal domains
  if (needsCrystal) {
    if (!policy.mattergen.enabled) {
      skipped.push({ service: 'mattergen', domain: 'inorganic_crystal', reason: 'org_policy' });
    } else if (policy.mattergen.weight === 0) {
      skipped.push({ service: 'mattergen', domain: 'inorganic_crystal', reason: 'zero_weight' });
    } else {
      const { variants, coverage } = selectMatterGenVariants(objectives, elements);
      const k = variants.length;
      const baseAlloc = Math.floor(domainBudget / k);
      let remainder = domainBudget - baseAlloc * k;

      for (const v of variants) {
        const budget = baseAlloc + (remainder > 0 ? 1 : 0);
        if (remainder > 0) remainder -= 1;
        calls.push({
          service: 'mattergen',
          tool: 'mattergen-service',
          variant: v,
          variantLabel: SERVICES.mattergen.variants[v]?.description || v,
          coveredObjectives: coverage[v] || [],
          budget: applyWeight(budget, policy.mattergen.weight),
          elements,
          objectives,
        });
      }
    }
  }

  // Molecular generation: GT4SD for property-conditioned, FM4M otherwise
  if (needsMolecular) {
    const domain = domains.includes('polymer') ? 'polymer' : 'small_molecule';
    let gt4sdVariant = domain === 'small_molecule' ? selectGT4SDVariant(objectives) : null;
    if (gt4sdVariant && !modelRuns(policy.gt4sd)) {
      skipped.push({
        service: 'gt4sd',
        domain,
        reason: policy.gt4sd.enabled ? 'zero_weight' : 'org_policy',
        fallback: modelRuns(policy.fm4m) ? 'fm4m' : null,
      });
      gt4sdVariant = null;
    }

    if (gt4sdVariant) {
      calls.push({
        service: 'gt4sd',
        tool: 'gt4sd-service',
        variant: gt4sdVariant,
        variantLabel: SERVICES.gt4sd.variants[gt4sdVariant]?.description || gt4sdVariant,
        coveredObjectives: objectives.filter(o => GT4SD_CONDITIONABLE_PROPS.has(o?.name || '')),
        budget: applyWeight(domainBudget, policy.gt4sd.weight),
        elements,
        objectives,
        domain,
      });
    } else if (!policy.fm4m.enabled) {
      skipped.push({ service: 'fm4m', domain, reason: 'org_policy' });
    } else if (policy.fm4m.weight === 0) {
      skipped.push({ service: 'fm4m', domain, reason: 'zero_weight' });
    } else {
      calls.push({
        service: 'fm4m',
        tool: 'ibm-materials-service',
        variant: null,
        variantLabel: domain === 'polymer' ? 'Polymer generation' : 'Small molecule generation',
        coveredObjectives: [],
        budget: applyWeight(domainBudget, policy.fm4m.weight),
        elements,
        objectives,
        domain,
      });
    }
  }

  return { calls, domains, totalBudget, skipped };
}

// Map a built-in variant of `service` to the single conditioning property a custom model
// must declare to auto-route runs of that variant. Pure lookup over SERVICES[service].variants;
// unconditional bases, unconditional services (fm4m/gt4sd), and unknown variants/services all
// return null (no auto-route). No LLM — deterministic.
function variantConditioningProperty(service, variant) {
  const svc = SERVICES[service];
  const v = svc && svc.variants ? svc.variants[variant] : null;
  if (!v) return null;
  if (Array.isArray(v.properties) && v.properties.length > 0) return v.properties[0];
  const req = Array.isArray(v.requires) ? v.requires : [];
  if (req.includes('elements')) return 'chemical_system';
  if (req.includes('space_group')) return 'space_group';
  return null;
}

// True iff `service` conditions generation on a fixed per-checkpoint property that a custom
// model can declare at registration (mattergen: one built-in checkpoint = one conditioning
// property, e.g. dft_band_gap ↔ band_gap). False for unconditional families (fm4m, gt4sd),
// whose custom-model auto-routing is family-capture instead of property-match.
//
// NOT implemented as "any variant has properties.length>0 || requires.length>0" — gt4sd's
// regression_transformer variant DOES carry a non-empty `properties` array
// (['logP','qed','sa_score','tpsa','binding_affinity','solubility']), but that list is a
// runtime target-selection menu (buildRow's gt4sdConditioningFromTargets picks ONE when a
// row is built), not a fixed
// per-variant conditioning gate compatible with the registration UI's conditioning vocabulary
// (which only ever offers mattergen's six properties). A naive variant scan would misclassify
// gt4sd as conditioning and contradict the design's Families table / Non-Goals. This is a
// literal per-family table instead. No LLM — deterministic.
const FAMILY_CONDITIONS = { mattergen: true, fm4m: false, gt4sd: false };
function familyConditions(service) {
  return !!FAMILY_CONDITIONS[service];
}

export { SERVICES, classifyDomains, selectMatterGenVariants, selectGT4SDVariant, GT4SD_CONDITIONABLE_PROPS, routeSpec, variantConditioningProperty, familyConditions, usableConditioningValue, mattergenConditioningValue };
